import { queryOptions, useQuery } from '@tanstack/react-query';

import { TimeRange, ApiResponse, QueryOptions } from '@cast/shared-types';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';
import { useRoxy } from 'features/db-optimization/context/RoxyContext';

const apiFn = apiClient.dbo.getCacheGroupPerformanceMetrics;

const DBO_CACHE_PERFORMANCE_METRICS_QUERY_KEY = 'dbo-cache-performance-metrics';

export type CacheGroupPerformanceMetricsQueryData = {
  items: ApiResponse<typeof apiFn>['items'];
  range: TimeRange;
  stepSeconds: number;
};

type CacheGroupPerformanceMetricsQueryArgs<SELECT> = QueryOptions<
  CacheGroupPerformanceMetricsQueryData,
  SELECT
> & {
  key: string;
  cacheGroupId?: string;
  cacheConfigId?: string;
  endpointName?: string;
  username?: string;
  stepSeconds?: number;
  getRange: () => TimeRange;
};

const getCacheGroupPerformanceMetricsQueryKey = (
  params: {
    key?: string;
    cacheGroupId?: string;
    cacheConfigId?: string;
    endpointName?: string;
    username?: string;
    stepSeconds?: number;
  } = {}
) =>
  [
    DBO_CACHE_PERFORMANCE_METRICS_QUERY_KEY,
    params.key,
    params.cacheGroupId,
    params.cacheConfigId,
    params.endpointName,
    params.username,
    params.stepSeconds,
  ] as const;

const getCacheGroupPerformanceMetricsQueryOptions = ({
  key,
  cacheGroupId,
  cacheConfigId,
  endpointName,
  username,
  stepSeconds = 60,
  getRange,
  enabled,
  roxy,
}: Omit<CacheGroupPerformanceMetricsQueryArgs<unknown>, 'select'> & {
  roxy?: boolean;
}) =>
  queryOptions({
    queryKey: getCacheGroupPerformanceMetricsQueryKey({
      key,
      cacheGroupId,
      cacheConfigId,
      endpointName,
      username,
      stepSeconds,
    }),
    queryFn: async () => {
      const range = getRange();
      const { data } = await apiFn({
        id: cacheGroupId!,
        databaseId: cacheConfigId,
        endpointName,
        username,
        startTime: range.from,
        endTime: range.to,
        stepSeconds: stepSeconds,
        roxy,
      });
      return { items: data.items, range, stepSeconds };
    },
    enabled: makeIsEnabled(!!cacheGroupId, enabled),
  });

export const useDbCacheGroupPerformanceMetricsQuery = <
  SELECT = CacheGroupPerformanceMetricsQueryData
>({
  key,
  cacheGroupId,
  cacheConfigId,
  endpointName,
  username,
  stepSeconds = 60,
  getRange,
  enabled,
  select,
  refetchInterval,
}: CacheGroupPerformanceMetricsQueryArgs<SELECT>) => {
  const roxy = useRoxy();

  return useQuery({
    ...getCacheGroupPerformanceMetricsQueryOptions({
      key,
      cacheGroupId,
      cacheConfigId,
      endpointName,
      username,
      stepSeconds,
      getRange,
      enabled,
      roxy,
    }),
    select,
    refetchInterval,
  });
};
